import { Injectable, NgZone, inject } from '@angular/core';
import {fetchAuthSession} from 'aws-amplify/auth';
import {Hub} from 'aws-amplify/utils';
import {defer, Observable, of, ReplaySubject, Subject} from 'rxjs';
import User, {JwtPayload} from './user';
import {catchError, map, mergeMap, tap} from 'rxjs/operators';
import {UserService} from './user.service';
import {Router} from '@angular/router';
import {AuthenticatorService} from '@aws-amplify/ui-angular';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private zone = inject(NgZone);
  private userService = inject(UserService);
  private router = inject(Router);
  private authenticatorService = inject(AuthenticatorService);

  private user$: Subject<User> = new ReplaySubject<User>(1);

  constructor() {
    this.registerOnAuthEvents();
    this.refresh().subscribe();
  }

  getUser$(): Observable<User> {
    return this.user$.asObservable();
  }

  getToken$(): Observable<string> {
    return defer(() => fetchAuthSession()).pipe(
      map((session) => session.tokens?.idToken?.toString()),
      catchError(() => of(null))
    );
  }

  isSignedIn(): boolean {
    return this.authenticatorService.route === 'authenticated';
  }

  logout(): void {
    this.authenticatorService.signOut();
    this.reset();
    this.router.navigateByUrl('/')
  }

  private registerOnAuthEvents(): void {
    Hub.listen('auth', (data) =>
      this.zone.run(() => this.handleAuthEvent(data.payload.event))
    );
  }

  private handleAuthEvent(event): void {
    if (event === 'signedIn') {
      this.refresh().subscribe(() => this.router.navigateByUrl('/'));
    } else if (event === 'tokenRefresh_failure') {
      this.logout();
    }
  }

  private refresh(): Observable<any> {
    return defer(() => fetchAuthSession()).pipe(
      map((session) => session.tokens.idToken),
      catchError(() => of(null)),
      mergeMap((token) =>
        // @ts-ignore
        this.userService.getMe$(token?.payload as JwtPayload)
      ),
      tap((user) => this.user$.next(user))
    );
  }

  private reset(): void {
    this.user$.next(User.anonymousUser());
  }
}
